"use client";
import { Button, Card, Flex, Form, Spin, Steps, Typography } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../../../assets/styles/Bond.css";
import cnt from "../../../services/definedConstant";
import { BOM } from "../../../services/definedMethod";
import { apiConfig } from "../../customer/customer.config";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import FeedbackModal from "../../../components/FeedbackModal";

import CustomerLookup from "./components/CustomerLookup";
import LoanParameters from "./components/LoanParameters";
import CollateralDocs from "./components/CollateralDocs";
import CreditAssessment from "./components/CreditAssessment";
import ApprovalWorkflow from "./components/ApprovalWorkflow";
import OfferAcceptance from "./components/OfferAcceptance";
import Disbursement from "./components/Disbursement";

const { Text } = Typography;

const stepConfig = [
  {
    title: "Customer Lookup",
    description: "KYC, credit score",
    tabLabel: "Customer",
    nextLabel: "Next: Loan Terms",
  },
  {
    title: "Loan Parameters",
    description: "Amount, tenor, product",
    tabLabel: "Loan Terms",
    nextLabel: "Next: Collateral & Docs",
  },
  {
    title: "Collateral & Documents",
    description: "Upload KYC, collateral, guarantor",
    tabLabel: "Collateral & Docs",
    nextLabel: "Next: Credit Check",
  },
  {
    title: "Credit Assessment",
    description: "Policy checks, risk scoring",
    tabLabel: "Credit Check",
    nextLabel: "Submit for approval",
  },
  {
    title: "Approval Workflow",
    description: "Officer → Manager → Credit committee",
    tabLabel: "Approval",
    nextLabel: "Generate offer letter",
  },
  {
    title: "Offer & Acceptance",
    description: "Loan offer letter, terms sign-off",
    tabLabel: "Offer Letter",
    nextLabel: "Proceed to disbursement",
  },
  {
    title: "Disbursement",
    description: "GL posting, account credit",
    tabLabel: "Disbursement",
    nextLabel: "Confirm disbursement",
  },
];

const breadcrumbItems = [
  { title: "Accounts & Contracts" },
  { title: "Loans" },
  { title: "New Application" },
];

const NewLoanOrigination = (props) => {
  const { userData } = props;
  const { session } = userData;

  const navigate = useNavigate();
  const [form] = useForm();

  const [current, setCurrent] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isStepDisabled] = useState(true);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [warningModalVisible, setWarningModalVisible] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [currencyList, setCurrencyList] = useState([]);
  const searchParam = Form.useWatch("customerId", form);

  const [formData, setFormData] = useState({
    customerLookup: null,
    loanParameters: null,
    collateralDocs: null,
    creditAssessment: null,
    approvalWorkflow: null,
    offerAcceptance: null,
    disbursement: null,
  });

  const [approvalState, setApprovalState] = useState(null);
  const approvalComplete = Boolean(approvalState?.complete);
  const approvalPending = approvalState?.chain
    ? approvalState.chain.filter(
        (stage) => stage.status === "active" || stage.status === "waiting"
      ).length
    : 5;

  const handleApprovalChange = useCallback((next) => {
    setApprovalState(next);
  }, []);

  useEffect(() => {
    const body = { session };
    BOM.FetchReqAction(body, apiConfig.Currencies, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err.defaultUserMessage);
        return;
      }
      const source = res?.selectedCurrencyOptions?.length
        ? res.selectedCurrencyOptions
        : res?.currencyOptions || [];
      setCurrencyList(
        source
          .filter((item) => item?.code)
          .map((item) => ({
            value: item.code,
            code: item.code,
            name: item.name || item.code,
            label: `${item.code} -- ${item.name || item.code}`,
          }))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applicationRef = "LN-APP-2026-0847";
  const overallProgress = Math.round(((current + 1) / stepConfig.length) * 100);

  const onSearch = () => {
    const query = (searchParam || "").trim();
    if (!query) {
      BOM.AlertMsg(cnt.WARNING, "Search parameter is required");
      return;
    }
    const params = {
      exactMatch: true,
      query,
      resource: "clients,clientIdentifiers",
    };
    const body = { session, params };

    setIsLoading(true);
    BOM.FetchReqAction(body, apiConfig.Search, (err, res) => {
      setIsLoading(false);
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Customer search failed"
        );
        return;
      }

      const list = Array.isArray(res) ? res : [];
      const match =
        list.find(
          (el) =>
            String(el?.entityAccountNo) === query ||
            String(el?.entityExternalId) === query ||
            String(el?.entityId) === query
        ) || list.find((el) => el?.entityId);

      if (!match) {
        BOM.AlertMsg(cnt.DANGER, "No matching customer found.");
        setSearchData(null);
        return;
      }

      setSearchData({
        entityId: match.entityId || "",
        entityAccountNo: match.entityAccountNo || "",
        entityName: match.entityName || "",
        entityMobileNo: match.entityMobileNo || "",
        entityType: match.customerType || match.entityType || "",
        entityExternalId: match.entityExternalId || "",
        entityBranch: match.parentName || "",
        entityStaff: match.staffName || "",
        entityStatus:
          typeof match.entityStatus === "string"
            ? match.entityStatus
            : match.entityStatus?.value || "",
      });
    });
  };

  const handleClose = () => {
    setSuccessModalVisible(false);
    setWarningModalVisible(false);
    navigate("/manage-loan-origination", { replace: false });
  };

  const onCancel = () => {
    navigate("/manage-loan-origination", { replace: false });
  };

  const nextButtonClick = () => {
    if (current === 4 && !approvalComplete) {
      BOM.AlertMsg(
        cnt.WARNING,
        "All approval stages must be signed off before generating the offer letter."
      );
      return;
    }
    form.submit();
  };

  const prevButtonClick = () => {
    setCurrent(current - 1);
  };

  const onFinishStep = (stepKey) => (values) => {
    setFormData((prev) => ({
      ...prev,
      [stepKey]: values,
    }));
    if (current < stepConfig.length - 1) {
      setCurrent(current + 1);
    }
  };

  const onSubmit = () => {
    setSuccessModalVisible(true);
  };

  const forms = [
    <CustomerLookup
      key="customer"
      onFinish={onFinishStep("customerLookup")}
      form={form}
      initialValues={formData.customerLookup}
      searchData={searchData}
      onSearch={onSearch}
      searchParam={searchParam}
      currencyList={currencyList}
    />,
    <LoanParameters
      key="params"
      onFinish={onFinishStep("loanParameters")}
      form={form}
      initialValues={formData.loanParameters}
      customerData={formData.customerLookup}
    />,
    <CollateralDocs
      key="docs"
      onFinish={onFinishStep("collateralDocs")}
      form={form}
      initialValues={formData.collateralDocs}
    />,
    <CreditAssessment
      key="credit"
      onFinish={onFinishStep("creditAssessment")}
      form={form}
      initialValues={formData.creditAssessment}
      customerData={searchData}
      loanData={formData.loanParameters}
    />,
    <ApprovalWorkflow
      key="approval"
      onFinish={onFinishStep("approvalWorkflow")}
      form={form}
      initialValues={formData.approvalWorkflow}
      savedState={approvalState}
      onChainChange={handleApprovalChange}
    />,
    <OfferAcceptance
      key="offer"
      onFinish={onFinishStep("offerAcceptance")}
      form={form}
      initialValues={formData.offerAcceptance}
      loanData={formData.loanParameters}
    />,
    <Disbursement
      key="disbursement"
      onFinish={onFinishStep("disbursement")}
      form={form}
      initialValues={formData.disbursement}
      loanData={formData.loanParameters}
    />,
  ];

  const isLastStep = current === forms.length - 1;

  return (
    <MainLayout pageItems={breadcrumbItems} onSave={onSubmit}>
      <TopContent
        pageTitle="Loan Origination"
        createPageTitle="New Application"
      />
      <Spin spinning={isLoading} size="large">
        <div
          style={{
            display: "flex",
            paddingLeft: 44,
            marginTop: 20,
            width: "100%",
            height: "calc(100vh - 160px)",
          }}
        >
          {/* Left Sidebar */}
          <div
            style={{
              width: 288,
              height: "100%",
              padding: "20px 0",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              {/* Application Header */}
              <div style={{ marginBottom: 24 }}>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    margin: 0,
                    marginBottom: 4,
                  }}
                >
                  Loan Application
                </h2>
                <div
                  style={{ fontSize: 13, color: "#667085", marginBottom: 8 }}
                >
                  {applicationRef}
                </div>
                <span
                  style={{
                    background: "#ecfdf5",
                    color: "#059669",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "3px 12px",
                    borderRadius: 12,
                    border: "1px solid #a7f3d0",
                  }}
                >
                  In progress
                </span>
              </div>

              {/* Stepper */}
              <Steps
                className="custom-step-style"
                current={current}
                onChange={(value) => setCurrent(value)}
                direction="vertical"
                style={{ height: "723px" }}
                items={stepConfig.map((step, index) => ({
                  title: (
                    <Text strong style={{ fontSize: 14 }}>
                      {index + 1} &middot; {step.title}
                    </Text>
                  ),
                  description: (
                    <span style={{ fontSize: 12, color: "#98A2B3" }}>
                      {step.description}
                    </span>
                  ),
                  disabled: isStepDisabled,
                }))}
              />
            </div>

            {/* Overall Progress */}
            <div style={{ paddingRight: 24, paddingTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                <span style={{ color: "#344054" }}>Overall progress</span>
                <span style={{ color: "#344054" }}>{overallProgress}%</span>
              </div>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: "#E4E7EC",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${overallProgress}%`,
                    height: "100%",
                    borderRadius: 3,
                    background: "#15CCAE",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <Card
            style={{
              borderRadius: 20,
              width: "100%",
              height: "100%",
              margin: 0,
              overflow: "hidden",
            }}
            bodyStyle={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              padding: "0 20px",
              overflow: "hidden",
            }}
          >
            {/* Tab Navigation */}
            <div
              style={{
                display: "flex",
                gap: 0,
                borderBottom: "1px solid #e5e7eb",
                marginBottom: 16,
                paddingTop: 4,
              }}
            >
              {stepConfig.map((step, index) => (
                <button
                  key={step.tabLabel}
                  onClick={() => setCurrent(index)}
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom:
                      current === index
                        ? "2px solid #15CCAE"
                        : "2px solid transparent",
                    padding: "12px 16px",
                    fontSize: 14,
                    fontWeight: current === index ? 600 : 400,
                    color: current === index ? "#344054" : "#667085",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {step.tabLabel}
                  {step.tabLabel === "Approval" && approvalPending > 0 && (
                    <span
                      style={{
                        background: "#15CCAE",
                        color: "white",
                        fontSize: 11,
                        fontWeight: 600,
                        padding: "1px 6px",
                        borderRadius: 10,
                        marginLeft: 6,
                      }}
                    >
                      {approvalPending}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Step subtitle */}
            <div
              style={{
                color: "#667085",
                fontSize: 14,
                marginBottom: 20,
              }}
            >
              {current === 0 &&
                "Search or look up an existing customer by ID, name, or account number"}
              {current === 1 &&
                "Select product, amount, tenor and review repayment schedule"}
              {current === 2 &&
                "Upload required KYC, collateral evidence, and guarantor details"}
              {current === 3 &&
                "Automated policy checks, risk scoring, and officer recommendation"}
              {current === 4 && "Multi-level maker-checker approval chain"}
              {current === 5 &&
                "Review and confirm final loan terms before disbursement"}
              {current === 6 &&
                "Final confirmation, GL posting, and account credit"}
            </div>

            {/* Form Content */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>{forms[current]}</div>

            {/* Navigation Footer */}
            <div
              style={{
                paddingTop: 24,
                paddingBottom: 16,
                borderTop: "1px solid #f2f4f7",
                marginTop: 24,
              }}
            >
              <Flex justify="space-between" align="center">
                <div>
                  {current === 0 ? (
                    <Button
                      style={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      className="previous-button"
                      onClick={() => setWarningModalVisible(true)}
                    >
                      Cancel
                    </Button>
                  ) : (
                    <Button
                      style={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      className="previous-button"
                      onClick={prevButtonClick}
                    >
                      &larr; Back
                    </Button>
                  )}
                </div>

                <Text style={{ color: "#667085", fontSize: 14 }}>
                  Step <strong>{current + 1}</strong> of {stepConfig.length}
                </Text>

                <div style={{ display: "flex", gap: 12 }}>
                  {current === 5 && (
                    <Button
                      style={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Download PDF
                    </Button>
                  )}
                  {current === 6 && (
                    <Button
                      style={{
                        backgroundColor: "white",
                        borderRadius: 8,
                        border: "1px solid #D0D5DD",
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Save as pending
                    </Button>
                  )}
                  {!isLastStep ? (
                    <Button
                      style={{
                        backgroundColor:
                          current === 4 && !approvalComplete
                            ? "#A7E8DE"
                            : "#15CCAE",
                        color: "white",
                        border: "1px solid",
                        borderColor:
                          current === 4 && !approvalComplete
                            ? "#A7E8DE"
                            : "#15CCAE",
                        borderRadius: 8,
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor:
                          current === 4 && !approvalComplete
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={nextButtonClick}
                    >
                      {stepConfig[current].nextLabel} &rsaquo;
                    </Button>
                  ) : (
                    <Button
                      style={{
                        backgroundColor: "#079455",
                        color: "white",
                        border: "1px solid #079455",
                        borderRadius: 8,
                        padding: "20px 24px",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                      onClick={onSubmit}
                    >
                      Confirm disbursement
                    </Button>
                  )}
                </div>
              </Flex>
            </div>
          </Card>
        </div>
      </Spin>

      {/* Success Modal */}
      <FeedbackModal
        visible={successModalVisible}
        type="success"
        onPrimaryAction={handleClose}
        onSecondaryAction={onCancel}
        onClose={handleClose}
        message="Loan has been disbursed successfully. All GL entries have been posted and the repayment schedule is now active."
      />

      {/* Warning Modal (cancel confirmation) */}
      <FeedbackModal
        visible={warningModalVisible}
        type="warning"
        onPrimaryAction={onCancel}
        onSecondaryAction={handleClose}
        onClose={handleClose}
      />
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(NewLoanOrigination);
